import { ThemeAppEmbedReadiness } from 'types/themeAppEmbed';

export type Status = ThemeAppEmbedReadiness['embedStatus'] | 'checking';
export type Evidence = { status: Status; editorUrl: string | null; reasonCode?: string };
const unavailable: Evidence = { status: 'unavailable', editorUrl: null };

export const themeAppEmbedActivationLabel = (status?: Status) => ({
  checking: 'Checking…', enabled: 'Enabled', disabled: 'Disabled', wrong_app: 'Action needed',
  unsupported: 'Unsupported', unavailable: 'Unknown',
}[status ?? 'unavailable']);

export const readThemeAppEmbedEvidence = (payload: unknown, storeId: string, shopDomain?: string): Evidence => {
  const value = (payload as { readiness?: Partial<ThemeAppEmbedReadiness> } | null)?.readiness;
  if (!value || value.schemaVersion !== 1 || value.storeId !== storeId ||
    value.embedHandle !== 'shipaid-widget') return unavailable;
  if (value.embedStatus === 'unsupported' && value.shopDomain === null && value.reasonCode === 'unsupported_store') {
    return { status: 'unsupported', editorUrl: null };
  }
  if (typeof shopDomain !== 'string' || typeof value.shopDomain !== 'string' ||
    !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(shopDomain) ||
    value.shopDomain.toLowerCase() !== shopDomain.toLowerCase()) return unavailable;
  const normalizedShopDomain = shopDomain.toLowerCase();

  const statuses = ['enabled', 'disabled', 'wrong_app', 'unavailable', 'unsupported'];
  if (!value.embedStatus || !statuses.includes(value.embedStatus)) return unavailable;
  const hasClientId = typeof value.installedAppClientId === 'string' && /^[A-Za-z0-9]{1,64}$/.test(value.installedAppClientId);
  const hasThemeId = typeof value.publishedThemeId === 'string' && /^\d+$/.test(value.publishedThemeId);
  if (['enabled', 'disabled', 'wrong_app'].includes(value.embedStatus) && (!hasClientId || !hasThemeId)) return unavailable;

  let editorUrl: string | null = null;
  if (hasClientId && value.embedStatus !== 'unsupported' && typeof value.editorUrl === 'string') {
    try {
      const url = new URL(value.editorUrl);
      const theme = hasThemeId ? value.publishedThemeId : 'current';
      const activation = url.searchParams.getAll('activateAppId');
      const safeAction = activation.length === 0 || (value.embedStatus === 'disabled' &&
        activation.length === 1 && activation[0] === `${value.installedAppClientId}/shipaid-widget`);
      if (url.origin === `https://${normalizedShopDomain}` && !url.username && !url.password && !url.hash &&
        url.pathname === `/admin/themes/${theme}/editor` &&
        url.searchParams.getAll('context').length === 1 && url.searchParams.get('context') === 'apps' &&
        safeAction &&
        Array.from(url.searchParams.keys()).every(key => ['context', 'activateAppId'].includes(key))) editorUrl = value.editorUrl;
    } catch {}
  }
  return { status: value.embedStatus, editorUrl, reasonCode: value.reasonCode };
};

export const themeAppEmbedMessage = (status: Status, expired: boolean, reasonCode?: string) => {
  if (status === 'checking') return 'Checking theme app embed…';
  if (status === 'enabled') return 'Theme app embed enabled on the published theme. Check cart placement and checkout behavior before going live.';
  if (status === 'disabled') return 'Theme app embed is disabled or has not been added to the published theme. Open the theme editor, enable ShipAid, and save.';
  if (status === 'wrong_app') return 'A ShipAid embed from a different app is present. Contact ShipAid to confirm the installation before changing existing embeds.';
  if (status === 'unsupported') return 'This installation cannot use the theme app embed. Contact ShipAid to confirm the supported installation path.';
  if (expired) return 'Theme app embed was not confirmed within 30 seconds. Retry the check or review it in your theme editor.';
  if (reasonCode === 'ownership_unverified') return 'Could not check whether the published theme embed belongs to this ShipAid installation. Open the theme editor or contact ShipAid for help.';
  return 'Could not check the theme app embed. Retry the check or contact ShipAid for installation help.';
};
